import {
	Component,
	OnInit,
	Input,
	OnDestroy,
	Output,
	EventEmitter,
	SimpleChanges,
	ViewEncapsulation,
	forwardRef,
	OnChanges
} from "@angular/core";
import { IdService } from "../shared/services/id.service";
import { FormControlBase } from "../shared/classes/form-control-base";
import { NG_VALUE_ACCESSOR } from "@angular/forms";

@Component({
	selector: "rbc-textarea",
	template: `
		<div class="rbc-textarea">
			<!-- Label -->
			<div #labelText class="input-label">
				<ng-content select="[label]"></ng-content>
			</div>
			<!-- Label text -->
			<rbc-label
				*ngIf="label"
				[id]="id + 'Label'"
				[for]="id"
				[text]="label"
				[required]="required && showRequired"
				[srOnly]="labelSrOnly">
			</rbc-label>

			<!-- Help text -->
			<rbc-help-text
				*ngIf="helpText"
				[id]="id + 'HelpText'"
				[text]="helpText"
				[srOnly]="helpTextSrOnly"
			></rbc-help-text>

			<textarea
				[id]="id"
				[attr.name]="name"
				[placeholder]="placeholder"
				[attr.aria-describedby]="describedbyGen()"
				[ngModel]="innerValue"
				(ngModelChange)="processChange($event)"
				[ngClass]="calculateClasses()"
				[attr.cols]= "cols ? cols : null"
				[attr.rows]= "rows ? rows : null"
				[attr.maxlength]= "maxlength ? maxlength : null"
				[attr.wrap]= "wrap ? wrap : null"
				[disabled]= "disabled"
				[attr.readonly]= "readonly ? '' : null"
				[attr.required]= "required ? '' : null"
				#textAreaElement
				(blur)="touch()"
				class="rbc-textarea-control"
			></textarea>

			<ng-container select="[status]">
				<rbc-status
					*ngIf="status"
					[id]="id + 'Status'"
					[text]="status"
					[type]="statusType">
					<ng-content select="[status-content]"></ng-content>
				</rbc-status>
			</ng-container>
		</div>
	`,
	styles: [],
	encapsulation: ViewEncapsulation.None,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: forwardRef(() => TextareaComponent)
		}
	]
})
export class TextareaComponent extends FormControlBase<string>
	implements OnInit, OnDestroy, OnChanges {
	// Private Members
	@Input() id: string;
	showError = false;

	// Input Parameters
	@Input() cols: number | false = false;
	@Input() disabled = false;
	@Input() errorLabel = "Invalid";
	@Input() helpText: string;
	@Input() label: string;
	@Input() maxlength: number;
	@Input() name: string;
	@Input() placeholder = "";
	@Input() readonly = false;
	@Input() required = false;
	@Input() showRequired: boolean;
	@Input() resizing = false;
	@Input() rows: number | false = false;
	@Input() wrap: "hard" | "soft";
	@Input() model: string;
	@Input() status: string;
	@Input() size: "small" | "medium" | "large" = "medium";
	@Input() statusType: string;
	@Input() triggerAfter = 0;
	@Input() labelSrOnly;
	@Input() helpTextSrOnly;
	// The event when the model is changed.
	@Output() modelChange = new EventEmitter();
	@Output() textAreaUpdate = new EventEmitter<any>(); // id and checked state object

	// Inject the ID service to generate a UUID
	constructor(private idService: IdService) {
		super();
		this.id = idService.generate();
	}

	ngOnChanges(changes: SimpleChanges) {
		super.ngOnChanges(changes);

		if (changes && changes.model) {
			this.writeValue(changes.model.currentValue);
		}
	}

	ngOnInit() {
		// The rows value is supposed to be a required field.
		if (this.rows === null) {
			throw new Error(
				"Define the number of rows of the textarea component " + this.id
			);
		}
	}

	ngOnDestroy(): void {
		// Remove the ID when the component gets destroyed, so the ID can be reused.
		this.idService.remove(this.id);
		super.ngOnDestroy();
	}

	/**
	 * Calculates the classes, primarily to determine if the class `resizeable`
	 * should be applied to the textarea element.
	 */
	calculateClasses(): {} {
		const classes = {};
		classes["resizable"] = this.resizing;
		classes["rbc-textarea-" + this.size] = this.size;
		classes[this.statusType] = this.statusType && this.status;
		return classes;
	}

	describedbyGen() {
		if (this.status || this.helpText) {
			const descriptors = {
				[`${this.id}Status`]: this.status,
				[`${this.id}HelpText`]: this.helpText
			};
			return Object.keys(descriptors)
				.filter(key => descriptors[key])
				.join(" ");
		} else {
			return null;
		}
	}

	/**
	 * When the ngModelChange event is fired, validation event is fired.
	 * Updated model gets emitted.
	 */
	processChange(value: string): void {
		this.showError = this.error();
		this.setModel(value);
		if (this.innerValue.length >= this.triggerAfter) {
			this.innerValue = value;
			this.modelChange.emit(this.innerValue);
			this.textAreaUpdate.emit({ id: this.id, value: this.innerValue });
		}
	}

	/**
	 * For validation purposes, currently the only validation for this field is required.
	 */
	error(): boolean {
		if (this.required) {
			return this.innerValue === "";
		}

		return false;
	}
}
